import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/base/tooltip';
import { CircleCheckIcon, InfoIcon } from '@ui/components/icons';

type FieldStatus = 'error' | 'success';

interface FieldStatusIndicatorProps {
  status: FieldStatus;
  children: ReactNode;
}

interface FieldStatusIconProps {
  status: FieldStatus;
}

const FieldStatusIcon = ({ status }: FieldStatusIconProps) =>
  status === 'success' ? (
    <CircleCheckIcon className="size-4 shrink-0 fill-current text-gain" />
  ) : (
    <InfoIcon className="size-4 shrink-0 fill-destructive" />
  );

const FieldStatusIndicator = ({ status, children }: FieldStatusIndicatorProps) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex cursor-pointer" />} data-status={status}>
        <FieldStatusIcon status={status} />
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <FieldStatusIcon status={status} />
        <span>{children}</span>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export interface FieldErrorIndicatorProps {
  messageId: string;
  values?: Record<string, string | number>;
}

const FieldErrorIndicator = ({ messageId, values }: FieldErrorIndicatorProps) => {
  const { formatMessage } = useIntl();

  return <FieldStatusIndicator status="error">{formatMessage({ id: messageId }, values)}</FieldStatusIndicator>;
};

export { FieldErrorIndicator, FieldStatusIndicator };
